import React, { useState, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ColDef } from "ag-grid-community";
import { Search } from "lucide-react";
import {
  Card,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Alert,
  Badge,
  Space,
  Divider,
  Row,
  Col,
  Statistic,
  notification,
  Tag,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  BellOutlined,
} from "@ant-design/icons";
import { Trash2, X as XIcon } from "lucide-react";
import dayjs from "dayjs";
import images from "../../../assets/images";
import "../../../styles/ag-grid-custom.css";
import {
  useStaffOfIndependentUnits,
  usePremisesStore,
} from "../../../store/store";
import {
  StaffOfIndependentUnits,
  CreateStaffFormData,
  UpdateStaffFormData,
  StaffIdentityDocument,
} from "../../../types/api";
import { useAuth } from "../../../auth/AuthProvider";
import type { RcFile, UploadFile } from "antd/es/upload/interface";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import { S3TempUrl } from "../../../utils/s3Client";

const getMimeType = (fileName: string) => {
  const ext = fileName.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "pdf":
      return "application/pdf";
    case "doc":
      return "application/msword";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "xls":
      return "application/vnd.ms-excel";
    case "xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    case "ppt":
      return "application/vnd.ms-powerpoint";
    case "pptx":
      return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "txt":
      return "text/plain";
    default:
      return "application/octet-stream";
  }
};

type IdentityUploadFile = UploadFile & { documentMeta?: StaffIdentityDocument };

const { Option } = Select;

const StaffManagement: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState<string>("");
  const [editingItem, setEditingItem] = useState<any>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPremise, setSelectedPremise] = useState<string>("");
  const [identityFileList, setIdentityFileList] = useState<
    IdentityUploadFile[]
  >([]);
  const [fileRemoved, setFileRemoved] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const gridRef = useRef<AgGridReact<StaffOfIndependentUnits>>(null);
  const [viewerFile, setViewerFile] = useState<{ url: string; name: string }>({
    url: "",
    name: "",
  });
  const { getsignedurl } = S3TempUrl();

  // Store hooks
  const {
    staff,
    loading: staffLoading,
    error: staffError,
    fetchStaff,
    createStaff,
    updateStaff,
    deleteStaff,
    searchStaff,
    fetchPremisesWithUnits,
    fetchIndependentUnits,
    independentUnits,
    premisesWithUnits,
  } = useStaffOfIndependentUnits();

  const { allPremises, fetchPremises } = usePremisesStore();

  // Auth hook
  const { user } = useAuth();

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchStaff(),
          fetchPremises(),
          fetchPremisesWithUnits(),
        ]);
      } catch (error) {
        message.error("Failed to load staff data");
      }
    };

    fetchData();
  }, [fetchStaff, fetchPremises, fetchPremisesWithUnits]);

  // Search staff
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchStaff();
      return;
    }

    try {
      setLoading(true);
      await searchStaff(searchQuery);
    } catch (error) {
      message.error("Failed to search staff");
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const totalStaff = staff.length;
  const activeStaff = staff.filter(
    (member) => member.status === "Active",
  ).length;

  const normalizeIdentityDocs = (docs: any): StaffIdentityDocument[] => {
    if (!docs) return [];
    let parsed = docs;
    if (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch (err) {
        return [];
      }
    }
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((doc) => ({
        name: doc.name || "",
        url: doc.url || "",
        uploadedAt: doc.uploadedAt || new Date().toISOString(),
        key: doc.key,
      }))
      .filter((doc) => doc.name || doc.url);
  };

  const showModal = (type: string, item?: any) => {
    setModalType(type);
    const normalizedItem = item
      ? {
        ...item,
        identityDocuments: normalizeIdentityDocs(item.identityDocuments),
      }
      : null;
    setEditingItem(normalizedItem);
    setIsModalVisible(true);
    if (item && normalizedItem) {
      const normalizedDocs = normalizedItem.identityDocuments;
      const existingDocs: IdentityUploadFile[] = normalizedDocs.map(
        (doc: StaffIdentityDocument, index: number) => ({
          uid: doc.key || doc.url || `${index}`,
          name: doc.name || `Document-${index + 1}`,
          status: "done",
          url: doc.url,
          documentMeta: doc,
        }),
      );
      setIdentityFileList(existingDocs);
      form.setFieldsValue({
        name: item.name,
        designation: item.designation,
        phone: item.phone,
        email: item.email,
        entity: item.entity,
        premise: item.premise,
        status: item.status,
        identityDocuments: existingDocs,
      });
      setSelectedPremise(item.premise);
      if (item.premise) {
        fetchIndependentUnits(item.premise);
      }
    } else {
      form.resetFields();
      form.setFieldsValue({ identityDocuments: [] });
      setIdentityFileList([]);
      setSelectedPremise("");
    }
    setFileRemoved(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingItem(null);
    form.resetFields();
    setSelectedPremise("");
    setIdentityFileList([]);
    setFileRemoved(false);
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const { identityDocuments: _identityDocuments, ...restValues } = values;

      // Validate premise ID format
      if (!restValues.premise || !/^[a-f\d]{24}$/i.test(restValues.premise)) {
        message.error("Invalid premise ID format");
        return;
      }

      // Find the selected premise data
      const selectedPremiseData = allPremises.find(
        (p) => p._id === restValues.premise,
      );
      if (!selectedPremiseData) {
        message.error("Selected premise not found");
        return;
      }

      const staffData = {
        ...restValues,
        region: selectedPremiseData.region || "default", // Add region from premise (required)
        lastUpdated: dayjs().format("YYYY-MM-DD"),
        createdAt: dayjs().format("YYYY-MM-DD"),
        createdBy: user?.email || "system", // Use actual user info
      };

      const formData = new FormData();
      Object.entries(staffData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(
            key,
            typeof value === "object" ? JSON.stringify(value) : String(value),
          );
        }
      });

      const existingDocumentsPayload = identityFileList
        .filter((file) => !file.originFileObj && file.documentMeta)
        .map((file) => file.documentMeta as StaffIdentityDocument);

      if (editingItem || existingDocumentsPayload.length > 0) {
        formData.append(
          "existingIdentityDocuments",
          JSON.stringify(existingDocumentsPayload),
        );
      }

      identityFileList
        .filter((file) => file.originFileObj)
        .forEach((file) => {
          const blob = file.originFileObj as RcFile;
          formData.append("identityDocuments", blob, blob.name);
        });

      if (editingItem) {
        formData.append("id", editingItem.id || editingItem._id);
        await updateStaff(editingItem.id || editingItem._id, formData);
      } else {
        await createStaff(formData);
      }

      fetchStaff();
      handleCancel();
    } catch (error) {
      message.error("Failed to save staff member");
    } finally {
      setLoading(false);
    }
  };

  const deleteStaffMember = async (staffId: string) => {
    try {
      await deleteStaff(staffId);
      fetchStaff();
    } catch (error) {
      message.error("Failed to delete staff member");
    }
  };

  const handleView = (staffMember: StaffOfIndependentUnits) => {
    showModal("view", staffMember);
  };

  const handleEdit = (staffMember: StaffOfIndependentUnits) => {
    showModal("edit", staffMember);
  };

  const handleDelete = (staffId: string) => {
    if (staffId) {
      deleteStaffMember(staffId);
    }
  };

  const updateIdentityFiles = (
    updater: (prev: IdentityUploadFile[]) => IdentityUploadFile[],
  ) => {
    setIdentityFileList((prev) => {
      const updated = updater(prev);
      form.setFieldsValue({ identityDocuments: updated });
      return updated;
    });
  };

  const appendIdentityFiles = (files: File[]) => {
    if (!files || files.length === 0) return;
    updateIdentityFiles((prev) => {
      const existingSignatures = new Set(
        prev.map(
          (file) => `${file.name}-${file.size || file.documentMeta?.url || ""}`,
        ),
      );
      const newEntries: IdentityUploadFile[] = [];

      files.forEach((file) => {
        const signature = `${file.name}-${file.size}`;
        if (!existingSignatures.has(signature)) {
          existingSignatures.add(signature);
          const uid = `${Date.now()}-${file.name}-${Math.random().toString(16).slice(2)}`;
          newEntries.push({
            uid,
            name: file.name,
            status: "done",
            size: file.size,
            type: file.type,
            originFileObj: file as unknown as RcFile,
          });
        }
      });

      return [...prev, ...newEntries];
    });
  };

  const handleIdentityInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    appendIdentityFiles(files);
    event.target.value = "";
  };

  const handleIdentityDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files
      ? Array.from(event.dataTransfer.files)
      : [];
    appendIdentityFiles(files);
  };

  const handleIdentityDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleRemoveIdentityFile = (uid: string) => {
    updateIdentityFiles((prev) => prev.filter((file) => file.uid !== uid));
    if (editingItem) {
      setFileRemoved(true);
    }
  };

  const openDocumentViewer = async (doc: StaffIdentityDocument) => {
    if (!doc.url && !doc.key) return;
    try {
      // Use key if available (S3 key), otherwise extract from URL
      // This matches FolderView.tsx pattern: file.originalName || file.name
      let fileName: string;
      if (doc.key) {
        fileName = doc.key;
      } else if (doc.url) {
        try {
          const urlObj = new URL(doc.url);
          fileName = decodeURIComponent(urlObj.pathname.slice(1));
        } catch {
          fileName = doc.name;
        }
      } else {
        fileName = doc.name;
      }

      if (!fileName) return;

      // Determine file type from file name (matching FolderView pattern)
      const fileType = getMimeType(fileName);

      // Get signed URL using the same pattern as FolderView
      const fileurl = await getsignedurl(fileName, fileType);

      // Use display name (doc.name) for viewer, matching FolderView pattern
      const displayName =
        doc.name && doc.name.trim().length > 0
          ? doc.name
          : fileName.split("/").pop() || "Document";

      setViewerFile({ url: fileurl, name: displayName });
    } catch (err) {
      message.error("Unable to open document");
    }
  };

  const closeDocumentViewer = () => {
    setViewerFile({ url: "", name: "" });
  };

  // Handle premise change to fetch independent units
  const handlePremiseChange = (premiseId: string) => {
    if (premiseId) {
      fetchIndependentUnits(premiseId);
      // Reset entity when premise changes
      form.setFieldsValue({ entity: undefined });
      setSelectedPremise(premiseId);
    }
  };

  const staffColumns: ColDef<StaffOfIndependentUnits>[] = [
    {
      headerName: "Name",
      field: "name",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
      headerName: "Designation",
      field: "designation",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
      headerName: "Phone",
      field: "phone",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
      headerName: "Email",
      field: "email",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
      headerName: "Entity",
      field: "entity",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
      cellRenderer: (params: any) => {
        const status = params.value;
        return (
          <div className="flex items-center">
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status === "Active"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
                }`}
            >
              <span
                className={`w-2 h-2 rounded-full mr-2 ${status === "Active" ? "bg-green-400" : "bg-gray-400"
                  }`}
              ></span>
              {status}
            </span>
          </div>
        );
      },
    },
    {
      headerName: "Last Updated",
      field: "lastUpdated",
      flex: 1,
      headerClass: "bg-[#e9f5ff]",
      cellStyle: { padding: "10px 10px 0 10px" },
      valueFormatter: (params) => {
        return dayjs(params.value).format("DD/MM/YYYY");
      },
    },
    {
      headerName: "Actions",
      flex: 1,
      headerClass: "bg-[#e9f5ff] text-center",
      cellStyle: { padding: "10px 10px 0 10px" },
      cellRenderer: (params: { data: StaffOfIndependentUnits }) => (
        <div className="flex items-center gap-2">
          <button onClick={() => handleView(params.data)}>
            <img className="iconTd" src={images.viewIcon} alt="View" />
          </button>
          <button onClick={() => handleEdit(params.data)}>
            <img className="iconTd" src={images.editIcon} alt="Edit" />
          </button>
          <button
            onClick={() => {
              const staffId = params.data.id || params.data._id;
              if (staffId) {
                handleDelete(staffId);
              }
            }}
            aria-label="Delete staff"
          >
            <Trash2 className="text-red-500" size={20} />
          </button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (staff.length > 0) {
      setTimeout(() => {
        gridRef.current?.api.sizeColumnsToFit();
      }, 0);
    }
  }, [staff]);

  return (
    <div className="px-2 py-4 max-w-8xl mx-auto mt-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-1">Staff Management</h2>
          <div className="text-gray-500 text-base">
            Manage staff members of independent units
          </div>
        </div>
        <button
          className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
          onClick={() => showModal("add")}
        >
          <img src={images.addPremiessIcon} width={25} alt="" />
          ADD STAFF MEMBER
        </button>
      </div>

      {/* Statistics Dashboard */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Staff"
              value={totalStaff}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Staff"
              value={activeStaff}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Inactive Staff"
              value={totalStaff - activeStaff}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#cf1322" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Entities"
              value={premisesWithUnits.reduce(
                (acc, premise) => acc + (premise.independentUnits?.length || 0),
                0,
              )}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Error Display */}
      {staffError && (
        <Alert
          message="Error"
          description={staffError}
          type="error"
          showIcon
          closable
          className="mb-6"
        />
      )}

      {/* Main Content */}
      <div className="bg-[#ffffff] rounded-md shadow-md">
        <div className="p-4 flex justify-between items-center gap-4">
          <div className="flex flex-1 max-w-md gap-2">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-500" />
              </div>
              <input
                type="text"
                placeholder="Search staff members..."
                className="block w-full pl-10 pr-3 py-3 border-2 border-[#a5b7cf] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                disabled={loading}
              />
            </div>
          </div>
        </div>

        <div className="ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            rowData={staff}
            columnDefs={staffColumns}
            rowHeight={50}
            headerHeight={65}
            domLayout="autoHeight"
            pagination={true}
            paginationPageSize={5}
            paginationPageSizeSelector={[5, 10, 20]}
            paginationAutoPageSize={false}
            defaultColDef={{
              sortable: true,
              resizable: true,
            }}
            loadingOverlayComponent={() => <div>Loading...</div>}
            loadingOverlayComponentParams={{}}
            overlayLoadingTemplate="<span class='ag-overlay-loading-center'>Loading...</span>"
            overlayNoRowsTemplate="<span class='ag-overlay-no-rows-center'>No staff members found</span>"
            loading={staffLoading}
          />
        </div>
      </div>

      {/* Staff Modal */}
      <Modal
        title={
          modalType === "add"
            ? "Add Staff Member"
            : modalType === "edit"
              ? "Edit Staff Member"
              : "View Staff Details"
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        className="rounded-lg overflow-hidden"
        maskClosable={false}
        zIndex={1000001}
        closable={false}
        styles={{
          header: {
            borderBottom: "2px solid #e5e7eb",
            borderRadius: "8px 8px 0 0",
          },
          body: {
            borderRadius: "0",
            padding: "0",
          },
        }}
      >
        {modalType === "view" && editingItem ? (
          <div className="p-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-lg text-gray-800 mb-4">
                Staff Details
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-600 font-semibold text-sm">
                    Name
                  </label>
                  <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                    {editingItem.name}
                  </p>
                </div>
                <div>
                  <label className="block text-gray-600 font-semibold text-sm">
                    Designation
                  </label>
                  <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                    {editingItem.designation}
                  </p>
                </div>
                <Row gutter={16}>
                  <Col span={12}>
                    <div>
                      <label className="block text-gray-600 font-semibold text-sm">
                        Phone
                      </label>
                      <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                        {editingItem.phone}
                      </p>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div>
                      <label className="block text-gray-600 font-semibold text-sm">
                        Email
                      </label>
                      <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                        {editingItem.email}
                      </p>
                    </div>
                  </Col>
                </Row>
                <div>
                  <label className="block text-gray-600 font-semibold text-sm">
                    Premise
                  </label>
                  <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                    {allPremises.find((p) => p._id === editingItem.premise)
                      ?.premisesName ||
                      editingItem.premise ||
                      "Not specified"}
                  </p>
                </div>
                <div>
                  <label className="block text-gray-600 font-semibold text-sm">
                    Entity (Independent Unit)
                  </label>
                  <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                    {editingItem.entity}
                  </p>
                </div>
                <div>
                  <label className="block text-gray-600 font-semibold text-sm">
                    Status
                  </label>
                  <div className="bg-gray-50 p-2 rounded border">
                    <Badge
                      status={
                        editingItem.status === "Active" ? "success" : "default"
                      }
                      text={editingItem.status}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-600 font-semibold text-sm">
                    Identity Documents / Verification Certificates
                  </label>
                  <div className="bg-gray-50 p-2 rounded border">
                    {editingItem.identityDocuments &&
                      editingItem.identityDocuments.length > 0 ? (
                      <Space direction="vertical">
                        {editingItem.identityDocuments.map(
                          (doc: StaffIdentityDocument, index: number) => (
                            <button
                              key={doc.url || index}
                              type="button"
                              onClick={() => openDocumentViewer(doc)}
                              className="text-[#0fa9a6] hover:underline text-left"
                            >
                              {doc.name || `Document ${index + 1}`}
                            </button>
                          ),
                        )}
                      </Space>
                    ) : (
                      <span className="text-gray-500">
                        No documents uploaded
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-gray-600 font-semibold text-sm">
                    Last Updated
                  </label>
                  <p className="text-gray-800 bg-gray-50 p-2 rounded border">
                    {dayjs(editingItem.lastUpdated).format("DD/MM/YYYY")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Please enter name" }]}
            >
              <Input placeholder="Enter full name" />
            </Form.Item>
            <Form.Item
              name="designation"
              label="Designation"
              rules={[{ required: true, message: "Please enter designation" }]}
            >
              <Input placeholder="Enter designation" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="phone"
                  label="Phone"
                  rules={[
                    { required: true, message: "Please enter phone number" },
                  ]}
                >
                  <Input placeholder="Enter phone number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[{ required: true, message: "Please enter email" }]}
                >
                  <Input placeholder="Enter email address" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="premise"
              label="Premise"
              rules={[{ required: true, message: "Please select premise" }]}
            >
              <Select
                placeholder="Select premise"
                value={selectedPremise}
                onChange={handlePremiseChange}
                disabled={allPremises.length === 0}
                notFoundContent={
                  allPremises.length === 0
                    ? "Loading premises..."
                    : "No premises found"
                }
                loading={allPremises.length === 0}
              >
                {allPremises.map((premise) => (
                  <Option key={premise._id} value={premise._id}>
                    {premise.premisesName}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="entity"
              label="Entity (Independent Unit)"
              rules={[{ required: true, message: "Please select entity" }]}
            >
              <Select
                placeholder="Select entity"
                notFoundContent="No entities found"
              >
                {independentUnits.map((unit) => (
                  <Option key={unit.id} value={unit.name}>
                    {unit.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: "Please select status" }]}
            >
              <Select placeholder="Select status">
                <Option value="Active">Active</Option>
                <Option value="Inactive">Inactive</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="identityDocuments"
              label="Identity Documents / Verification Certificates"
            >
              {editingItem && identityFileList.length > 0 && (
                <div className="space-y-2 mb-4">
                  {identityFileList.map((file) => {
                    const canPreview = Boolean(file.documentMeta?.url);
                    const displayName = file.name;
                    return (
                      <div
                        key={file.uid}
                        className="flex items-center border border-gray-200 rounded-md bg-gray-50 px-3 py-2"
                      >
                        {canPreview ? (
                          <button
                            type="button"
                            onClick={() =>
                              openDocumentViewer(
                                file.documentMeta as StaffIdentityDocument,
                              )
                            }
                            className="text-[#0fa9a6] hover:underline text-left truncate flex-1"
                            title={displayName}
                          >
                            {displayName}
                          </button>
                        ) : (
                          <span
                            className="text-gray-600 truncate flex-1"
                            title={displayName}
                          >
                            {displayName}
                          </span>
                        )}
                        <button
                          type="button"
                          className="ml-2 text-red-500 hover:text-red-600"
                          onClick={() => handleRemoveIdentityFile(file.uid)}
                          aria-label="Remove document"
                        >
                          <XIcon className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {(!editingItem ||
                fileRemoved ||
                (editingItem && identityFileList.length === 0)) && (
                  <div
                    className="mb-4 border-dashed border-2 border-gray-300 rounded-md p-6 text-center cursor-pointer hover:border-[#0fa9a6] transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleIdentityDragOver}
                    onDrop={handleIdentityDrop}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      hidden
                      multiple
                      accept=".docx,.pdf,.txt,.jpg,.png,.ppt,.pptx"
                      onChange={handleIdentityInputChange}
                      aria-hidden="true"
                      tabIndex={-1}
                      id="identityDocumentsInput"
                    />
                    <div className="cursor-pointer block">
                      {identityFileList.length > 0 ? (
                        <div className="space-y-1">
                          {identityFileList.map((file) => {
                            const url = file.documentMeta?.url;
                            return url ? (
                              <a
                                key={file.uid}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[#0fa9a6] hover:underline truncate mx-auto"
                                title={file.name}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {file.name}
                              </a>
                            ) : (
                              <p
                                key={file.uid}
                                className="text-gray-600 truncate mx-auto"
                                title={file.name}
                              >
                                {file.name}
                              </p>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-gray-700 font-semibold">
                          Drag & Drop Your Files Here Or Click
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Supported formats: DOCX, PDF, TXT, JPG, PNG, PPT, PPTX (Max:
                      5MB)
                    </p>
                  </div>
                )}
            </Form.Item>
          </Form>
        )}

        {/* Custom Footer with Styled Buttons */}
        <div className="flex bg-[#61748E] px-3 py-2 justify-end gap-4 -mx-6 -mb-6">
          {modalType === "view" ? (
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 transition-colors"
            >
              Close
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-400 text-white rounded-full hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => form.submit()}
                disabled={loading}
                className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 transition-colors disabled:opacity-50"
              >
                {loading
                  ? editingItem
                    ? "Updating..."
                    : "Adding..."
                  : editingItem
                    ? "Update"
                    : "Add"}
              </button>
            </>
          )}
        </div>
      </Modal>
      {viewerFile.url !== "" && (
        <div
          className="absmiddle"
          style={{
            width: "100vw",
            height: "100vh",
            zIndex: 999999,
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
        >
          <XIcon
            className="absolute left-auto top-2 z-10 cursor-pointer bg-red-500 text-white"
            style={{ right: "115px" }}
            onClick={closeDocumentViewer}
            aria-label="Close document viewer"
          />
          <div
            className="absmiddle bg-white border py-9"
            style={{
              width: "calc(100vw - 200px)",
              height: "100vh",
              overflow: "auto",
              boxSizing: "border-box",
            }}
          >
            <DocumentViewer file={viewerFile.url} name={viewerFile.name} />
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffManagement;
